// Big Blue FishIQ presentation controller.
// Owns only Leaflet marker presentation/cache/scheduling. FishIQ reads, ranking,
// search-lure economy, persistence, and validation remain owned by MapView.

// @ts-ignore - local editor type noise for leaflet; runtime import is valid in this project
import L from "leaflet";
import type { Hotspot } from "../../../types";
import roddoFish from "../../../assets/roddo-fish.png";
import { createBigBlueDivIcon } from "../mapIcons";
import { BIG_BLUE_MAP_PANES, getBigBlueMapPane } from "../mapPanes";

export type FishIQHubKind = "history" | "area";
export type FishIQMapPoint = { x: number; y: number };
export type FishIQLatLng = { lat: number; lng: number };

export type FishIQCollisionResolver = (
  desired: FishIQMapPoint,
  occupied: FishIQMapPoint[],
  size: any,
  opts?: { minDistance?: number; seed?: number; maxRadius?: number },
) => FishIQMapPoint;

export type FishIQLayerRenderOptions = {
  map: any;
  layer: any;
  spots: Hotspot[];
  dark: boolean;
  fishWidgetAnchor: FishIQLatLng | null;
  latestUserLatLng: FishIQLatLng | null;
  mapActionAnchor: FishIQLatLng | null;
  promotedMapActionAnchor: FishIQLatLng | null;
  uniqueFishCount: number;
  getOccupiedPoints: () => FishIQMapPoint[];
  resolveCollision: FishIQCollisionResolver;
  hashString: (value: string) => number;
  onHubClick: (kind: FishIQHubKind) => void;
};

export type FishIQLayerController = {
  schedule: (
    options: FishIQLayerRenderOptions,
    force?: boolean,
    opts?: { invalidateVersion?: boolean },
  ) => void;
  clear: (
    map: any,
    layer: any,
    opts?: { resetSignature?: boolean; invalidateVersion?: boolean },
  ) => void;
  refreshSelection: (state: {
    open: boolean;
    mode: FishIQHubKind;
    hasSelectedFish: boolean;
  }) => void;
  forEachMarker: (visitor: (marker: any, kind: FishIQHubKind) => void) => void;
  removeAreaHub: (map: any, layer: any) => void;
  dispose: (map: any, layer: any) => void;
};

const HUB_KINDS: FishIQHubKind[] = ["history", "area"];

const createFishHubIcon = (
  kind: FishIQHubKind,
  dark: boolean,
  selected = false,
  count = 0,
) => {
  const size = selected ? 48 : 42;
  const shell = dark
    ? selected ? "#46568F" : "#33436F"
    : selected ? "#1677F2" : "#2176E8";
  const shellDeep = dark
    ? selected ? "#263259" : "#1B2647"
    : selected ? "#0756C8" : "#0B5ED1";
  const shadow = selected
    ? "0 7px 15px rgba(15,23,42,.28),0 0 0 3px rgba(60,151,255,.16)"
    : "0 5px 11px rgba(15,23,42,.22)";
  const safeCount = Math.max(0, Math.min(99, Math.round(Number(count) || 0)));
  const countText = safeCount > 0 ? String(safeCount) : "";

  return createBigBlueDivIcon({
    className: "roddo-map-fish-hub-icon",
    iconSize: [size + 8, size + 9],
    iconAnchor: [Math.round((size + 8) / 2), size + 6],
    popupAnchor: [0, -(size - 2)],
    html: `
      <div class="roddo-fish-hub-entry" data-kind="${kind}" style="position:relative;width:${size + 8}px;height:${size + 9}px;overflow:visible;">
        <div style="position:absolute;left:50%;top:1px;width:${size - 5}px;height:${size - 5}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,${shell} 0%,${shellDeep} 78%);border:2px solid ${dark ? 'rgba(190,211,244,.58)' : 'rgba(255,255,255,.96)'};box-shadow:${shadow};">
          <div style="position:absolute;inset:5px;border-radius:999px;background:${dark ? "#0D1627" : "#FFFDF7"};border:1px solid ${dark ? "rgba(176,202,244,.14)" : "rgba(255,255,255,.86)"};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;">
            <img src="${roddoFish}" alt="" draggable="false" style="width:${selected ? 30 : 27}px;height:${selected ? 30 : 27}px;object-fit:contain;display:block;transform:translateY(-4px);" />
          </div>
        </div>
        ${countText ? `<div aria-label="${countText} fish found" style="position:absolute;right:0;top:-2px;min-width:18px;height:18px;padding:0 4px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:${dark ? "#0B1324" : "#FFFFFF"};border:2px solid ${shell};color:${dark ? "#CFE2FF" : shellDeep};font-size:9px;font-weight:950;line-height:1;box-sizing:border-box;box-shadow:0 3px 7px rgba(15,23,42,.18);">${countText}</div>` : ""}
      </div>`,
  });
};

export const createFishIQLayerController = (): FishIQLayerController => {
  let raf: number | null = null;
  let renderVersion = 0;
  let lastSignature = "";
  const markers: Record<FishIQHubKind, any | null> = {
    history: null,
    area: null,
  };

  const cancelScheduled = () => {
    if (raf != null && typeof window !== "undefined") {
      window.cancelAnimationFrame(raf);
    }
    raf = null;
  };

  const removeMarker = (kind: FishIQHubKind, layer?: any) => {
    const marker = markers[kind];
    if (!marker) return;
    try { marker.off?.(); } catch {}
    try { layer?.removeLayer?.(marker); } catch {}
    try { marker.remove?.(); } catch {}
    markers[kind] = null;
  };

  const clearDomResidue = (map: any) => {
    try {
      const markersPane = getBigBlueMapPane(map, BIG_BLUE_MAP_PANES.markers);
      markersPane
        ?.querySelectorAll?.(
          ".leaflet-marker-icon.roddo-map-fish-hub-icon, .leaflet-marker-shadow.roddo-map-fish-hub-icon, .roddo-map-fish-hub-icon, .roddo-fish-hub-entry",
        )
        ?.forEach?.((node: Element) => {
          try { node.remove(); } catch {}
        });
    } catch {}
  };

  const clear: FishIQLayerController["clear"] = (map, layer, opts) => {
    cancelScheduled();
    if (opts?.invalidateVersion !== false) renderVersion += 1;

    try { layer?.clearLayers?.(); } catch {}
    HUB_KINDS.forEach((kind) => removeMarker(kind, layer));
    clearDomResidue(map);
    if (opts?.resetSignature !== false) lastSignature = "";
  };

  const update = (options: FishIQLayerRenderOptions, force: boolean, activeVersion: number) => {
    const {
      map,
      layer,
      spots,
      dark,
      fishWidgetAnchor,
      latestUserLatLng,
      mapActionAnchor,
      promotedMapActionAnchor,
      uniqueFishCount,
      getOccupiedPoints,
      resolveCollision,
      hashString,
      onHubClick,
    } = options;

    if (!map || !layer || activeVersion !== renderVersion) return;

    const unique = (() => {
      const seen = new Set<string>();
      return spots.filter((spot) => {
        const key = String(spot?.species || spot?.name || spot?.id || "")
          .trim()
          .toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })();

    if (!unique.length) {
      clear(map, layer, { resetSignature: force, invalidateVersion: false });
      return;
    }

    const primary = unique[0] || spots[0];
    const secondary = unique[1] || unique[0] || spots[0];
    const primaryPoint = map.latLngToContainerPoint([
      primary.position.lat,
      primary.position.lng,
    ]);
    const secondaryBasePoint = map.latLngToContainerPoint([
      secondary.position.lat,
      secondary.position.lng,
    ]);

    const size = map.getSize?.();
    const occupiedPoints = size ? getOccupiedPoints() : [];

    const exactAreaAnchor =
      fishWidgetAnchor &&
      Number.isFinite(Number(fishWidgetAnchor.lat)) &&
      Number.isFinite(Number(fishWidgetAnchor.lng))
        ? fishWidgetAnchor
        : null;
    const desiredAreaPoint = exactAreaAnchor
      ? map.latLngToContainerPoint([exactAreaAnchor.lat, exactAreaAnchor.lng])
      : unique.length > 1
        ? { x: secondaryBasePoint.x + 28, y: secondaryBasePoint.y - 8 }
        : { x: primaryPoint.x + 76, y: primaryPoint.y - 14 };

    let areaPoint = desiredAreaPoint;
    let visualAreaLatLng: FishIQLatLng | null = exactAreaAnchor
      ? { lat: Number(exactAreaAnchor.lat), lng: Number(exactAreaAnchor.lng) }
      : null;

    if (exactAreaAnchor && size && latestUserLatLng) {
      try {
        const userPoint = map.latLngToContainerPoint([
          latestUserLatLng.lat,
          latestUserLatLng.lng,
        ]);
        const dx = Number(desiredAreaPoint.x) - Number(userPoint.x);
        const dy = Number(desiredAreaPoint.y) - Number(userPoint.y);
        const userDistancePx = Math.hypot(dx, dy);

        if (Number.isFinite(userDistancePx) && userDistancePx < 68) {
          areaPoint = resolveCollision(
            { x: Number(desiredAreaPoint.x), y: Number(desiredAreaPoint.y) },
            [{ x: Number(userPoint.x), y: Number(userPoint.y) }],
            size,
            {
              minDistance: 74,
              maxRadius: 92,
              seed: hashString(
                `fishiq-user-clearance:${Number(exactAreaAnchor.lat).toFixed(4)}:${Number(exactAreaAnchor.lng).toFixed(4)}`,
              ),
            },
          );
          const shifted = map.containerPointToLatLng([areaPoint.x, areaPoint.y]);
          visualAreaLatLng = { lat: Number(shifted.lat), lng: Number(shifted.lng) };
        }
      } catch {}
    } else if (!exactAreaAnchor && size) {
      areaPoint = resolveCollision(
        { x: desiredAreaPoint.x, y: desiredAreaPoint.y },
        occupiedPoints,
        size,
        {
          minDistance: 72,
          maxRadius: 96,
          seed: hashString("fishiq-area-fallback"),
        },
      );
    }

    // The working-point marker owns FishIQ presentation whenever its anchor is
    // the same FishIQ read area (within the canonical 80 m promotion radius).
    // Derive ownership from the live anchors instead of the promotion ref so a
    // React commit cannot briefly leave both the POI/FishIQ marker and area hub
    // visible at the same time.
    const mapActionOwnsAreaHub = Boolean(
      mapActionAnchor &&
        exactAreaAnchor &&
        unique.length > 0 &&
        (() => {
          try {
            const distanceMeters = Number(
              map.distance?.(
                [Number(mapActionAnchor.lat), Number(mapActionAnchor.lng)],
                [Number(exactAreaAnchor.lat), Number(exactAreaAnchor.lng)],
              ),
            );
            if (Number.isFinite(distanceMeters)) return distanceMeters <= 80;
          } catch {}

          // Fallback for lightweight/mock map implementations that do not expose
          // Leaflet's distance() helper. The prior promoted anchor remains a safe
          // secondary signal, but no longer drives normal runtime ownership.
          return Boolean(promotedMapActionAnchor);
        })(),
    );

    const nextMarkers = mapActionOwnsAreaHub
      ? []
      : [
          {
            kind: "area" as const,
            point: areaPoint,
            latLng: visualAreaLatLng,
            selected: false,
            count: uniqueFishCount || unique.length,
          },
        ];

    const signature = JSON.stringify({
      dark,
      markers: nextMarkers.map(({ kind, point, latLng, count }) => ({
        kind,
        count,
        ...(latLng
          ? { lat: Number(latLng.lat).toFixed(6), lng: Number(latLng.lng).toFixed(6) }
          : { x: Math.round(point.x), y: Math.round(point.y) }),
      })),
    });

    if (!force && signature === lastSignature) return;

    clear(map, layer, { resetSignature: false, invalidateVersion: false });
    if (activeVersion !== renderVersion) return;

    nextMarkers.forEach(({ kind, point, latLng: lockedLatLng, selected, count }) => {
      if (activeVersion !== renderVersion) return;

      const latLng = lockedLatLng
        ? [lockedLatLng.lat, lockedLatLng.lng]
        : map.containerPointToLatLng([point.x, point.y]);
      const marker = (L as any).marker(latLng, {
        icon: createFishHubIcon(kind, dark, selected, count),
        pane: BIG_BLUE_MAP_PANES.markers,
        keyboard: false,
        riseOnHover: true,
        zIndexOffset: selected ? 1300 : 1120,
      });

      marker.on("click", () => onHubClick(kind));
      markers[kind] = marker;
      layer.addLayer(marker);
    });

    lastSignature = signature;
  };

  const schedule: FishIQLayerController["schedule"] = (options, force = false, opts) => {
    cancelScheduled();
    if (opts?.invalidateVersion !== false) renderVersion += 1;
    const activeVersion = renderVersion;
    const nextOptions = { ...options, spots: [...options.spots] };

    if (typeof window === "undefined") {
      update(nextOptions, force, activeVersion);
      return;
    }

    raf = window.requestAnimationFrame(() => {
      raf = null;
      update(nextOptions, force, activeVersion);
    });
  };

  const refreshSelection: FishIQLayerController["refreshSelection"] = ({
    open,
    mode,
    hasSelectedFish,
  }) => {
    const selectedState = {
      history: !!(open && mode === "history" && !hasSelectedFish),
      area: !!(open && mode === "area" && !hasSelectedFish),
    };

    HUB_KINDS.forEach((kind) => {
      const marker = markers[kind];
      if (!marker) return;
      try {
        marker.setZIndexOffset?.(
          selectedState[kind] ? 1300 : kind === "history" ? 1080 : 1060,
        );
      } catch {}
    });
  };

  const forEachMarker: FishIQLayerController["forEachMarker"] = (visitor) => {
    HUB_KINDS.forEach((kind) => {
      const marker = markers[kind];
      if (marker) visitor(marker, kind);
    });
  };

  const removeAreaHub: FishIQLayerController["removeAreaHub"] = (map, layer) => {
    removeMarker("area", layer);
    lastSignature = "";
    clearDomResidue(map);
  };

  const dispose: FishIQLayerController["dispose"] = (map, layer) => {
    clear(map, layer, { resetSignature: true, invalidateVersion: true });
  };

  return {
    schedule,
    clear,
    refreshSelection,
    forEachMarker,
    removeAreaHub,
    dispose,
  };
};
